// @ts-nocheck
/* Verbatim port of v1 utilities/userCreation.js */
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_ACL_SETTING_LIST,
  API_SIGNUP_EMAIL_SEND_CODE,
  API_SIGNUP_EMAIL_VERIFY_CODE,
} from '@/service/url';
import {
  validateEmail,
  getUsernameValidationError,
  getNameValidationError,
  getUsernameTakenMessage,
} from './helpers';

export {
  validateEmail,
  getUsernameValidationError,
  getNameValidationError,
  getUsernameTakenMessage,
};

export const MIN_PASSWORD_LENGTH = 8;

export const PASSWORD_SPECIAL_CHAR_REGEX = /[!@#$%^&*(),.?":{}|<>]/;

export const PASSWORD_RULES = [
  {
    key: 'length',
    label: `At least ${MIN_PASSWORD_LENGTH} characters`,
    test: (value) => value.length >= MIN_PASSWORD_LENGTH,
  },
  { key: 'upper', label: 'At least 1 uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { key: 'lower', label: 'At least 1 lowercase letter', test: (value) => /[a-z]/.test(value) },
  { key: 'number', label: 'At least 1 number', test: (value) => /[0-9]/.test(value) },
  {
    key: 'special',
    label: 'At least 1 special character',
    test: (value) => PASSWORD_SPECIAL_CHAR_REGEX.test(value),
  },
  { key: 'space', label: 'No spaces allowed', test: (value) => !/\s/.test(value) },
];

export function getPasswordRuleFlags(value = '') {
  const input = String(value ?? '');
  return PASSWORD_RULES.reduce((acc, rule) => {
    acc[rule.key] = rule.test(input);
    return acc;
  }, {});
}

export function getPasswordValidationErrors(value = '') {
  const input = String(value ?? '');
  return PASSWORD_RULES.filter((rule) => !rule.test(input)).map((rule) => rule.label);
}

export function isPasswordValid(value = '') {
  return getPasswordValidationErrors(value).length === 0;
}

const PW_SETS = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnpqrstuvwxyz', '23456789', '!@#$%&*'];

export function generateRandomPassword(length = 12) {
  const all = PW_SETS.join('');
  const rand = (n) => crypto.getRandomValues(new Uint32Array(1))[0] % n;
  const chars = PW_SETS.map((set) => set[rand(set.length)]);
  while (chars.length < length) chars.push(all[rand(all.length)]);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rand(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

export function getConfirmPasswordError(password = '', confirmPassword = '') {
  if (!String(confirmPassword).trim()) return 'Confirm password is required';
  if (String(password) !== String(confirmPassword)) return 'Passwords do not match';
  return '';
}

export function pickDefaultAclId(aclList = [], role = '') {
  if (!role) return '';
  const defaultAcl = (aclList || []).find((acl) => acl.default_for_role === role);
  return defaultAcl ? String(defaultAcl.id) : '';
}

export function fetchAclListForRole(role) {
  return getApiCall(`${API_ACL_SETTING_LIST}?target_role=${encodeURIComponent(role)}&fields=min`, {});
}

export async function loadAclOptionsForRole(role) {
  if (!role) return { list: [], defaultAclId: '' };
  const list = (await fetchAclListForRole(role)) || [];
  return { list, defaultAclId: pickDefaultAclId(list, role) };
}

export function sendSignupEmailCode(email) {
  return postApiCall(API_SIGNUP_EMAIL_SEND_CODE, { email: String(email || '').trim() });
}

export async function verifySignupEmailOtp(email, otp) {
  const data = await postApiCall(API_SIGNUP_EMAIL_VERIFY_CODE, {
    email: String(email || '').trim(),
    otp: String(otp || '').trim(),
  });
  return data?.signupEmailToken || '';
}

function withPrefix(prefix, sentence) {
  if (!prefix) return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  return `${prefix}${sentence}`;
}

export function validateUserCoreFields({
  name,
  username,
  email,
  password,
  confirmPassword,
  existingUsernames = [],
  role,
  aclId,
  requireRole = true,
  requireAcl = true,
  emailVerificationRequired = false,
  emailVerified = false,
  signupEmailToken = '',
  labelPrefix = '',
  usernameStatus = 'idle',
  emailStatus = 'idle',
} = {}) {
  const result = {
    valid: true,
    nameError: '',
    usernameError: '',
    usernameTakenMessage: '',
    emailError: '',
    emailTakenMessage: '',
    passwordError: '',
    passwordRuleErrors: [],
    confirmPasswordError: '',
    roleError: '',
    aclError: '',
    emailVerifyError: '',
    messages: [],
  };

  const fail = (field, message) => {
    result.valid = false;
    result[field] = message;
    result.messages.push(message);
  };

  const nameError = getNameValidationError(name);
  if (nameError) fail('nameError', nameError);

  const usernameError = getUsernameValidationError(username);
  if (usernameError) {
    fail('usernameError', usernameError);
  } else {
    const takenMessage = getUsernameTakenMessage(username, existingUsernames);
    if (takenMessage) fail('usernameTakenMessage', takenMessage);
    else if (usernameStatus === 'taken') fail('usernameTakenMessage', 'Username already exists');
  }

  if (!String(email || '').trim() || validateEmail(email)) {
    fail('emailError', withPrefix(labelPrefix, 'email is required and must be valid'));
  } else if (emailStatus === 'taken') {
    fail('emailTakenMessage', withPrefix(labelPrefix, 'email already exists'));
  }

  result.passwordRuleErrors = getPasswordValidationErrors(password);
  if (!String(password || '').trim()) {
    fail('passwordError', withPrefix(labelPrefix, 'password is required'));
  } else if (result.passwordRuleErrors.length > 0) {
    fail(
      'passwordError',
      withPrefix(labelPrefix, `password: ${result.passwordRuleErrors[0]}`),
    );
  }

  const confirmError = getConfirmPasswordError(password, confirmPassword);
  if (confirmError) {
    fail('confirmPasswordError', withPrefix(labelPrefix, confirmError.toLowerCase()));
  }

  if (requireRole && !role) {
    fail('roleError', withPrefix(labelPrefix, 'role is required'));
  }

  if (requireAcl && !aclId) {
    fail('aclError', withPrefix(labelPrefix, 'ACL settings are required'));
  }

  if (emailVerificationRequired && (!emailVerified || !signupEmailToken)) {
    fail(
      'emailVerifyError',
      withPrefix(
        labelPrefix,
        'email must be verified: use Send code, enter the OTP, then click Verify OTP.',
      ),
    );
  }

  return result;
}
